import { FileTextOutlined, ReloadOutlined } from "@ant-design/icons"
import { Button, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

const { Text } = Typography

type InvoicesEmptyVariant = "none" | "filtered" | "error"

interface InvoicesEmptyStateProps {
  variant: InvoicesEmptyVariant
  canCreate?: boolean
  onCreateInvoice?: () => void
  /** Wraps the create button, for a create that first asks what to add. */
  renderCreate?: (button: React.ReactElement) => React.ReactElement
  onClearFilters?: () => void
  onRetry?: () => void
}

/**
 * A compact, centered stand-in for antd's default `Empty` — whose big
 * illustration and free-floating padding stretched to fill the fixed-column
 * table's synced-scroll height, producing a near full-page blank rectangle
 * behind three lines of text.
 */
export function InvoicesEmptyState({
  variant,
  canCreate,
  onCreateInvoice,
  renderCreate,
  onClearFilters,
  onRetry
}: InvoicesEmptyStateProps) {
  const { t } = useTranslation()

  const isError = variant === "error"
  const isFiltered = variant === "filtered"

  return (
    <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 px-6 py-6 text-center">
      <div
        className={`flex size-12 items-center justify-center rounded-full text-xl ${
          isError ? "bg-error-bg text-error" : "bg-primary-bg text-primary"
        }`}
      >
        <FileTextOutlined />
      </div>
      <Text strong>
        {t(
          isError
            ? "invoices.error"
            : isFiltered
              ? "invoices.emptyFiltered"
              : "invoices.empty"
        )}
      </Text>
      {!isError && (
        <Text type="secondary" className="max-w-xs">
          {t(
            isFiltered
              ? "invoices.emptyFilteredDescription"
              : "invoices.emptyDescription"
          )}
        </Text>
      )}
      {isError && (
        <Button icon={<ReloadOutlined />} onClick={onRetry}>
          {t("invoices.retry")}
        </Button>
      )}
      {isFiltered && onClearFilters && (
        <Button onClick={onClearFilters}>{t("invoices.clearFilters")}</Button>
      )}
      {!isError &&
        !isFiltered &&
        canCreate &&
        (() => {
          const button = (
            <Button type="primary" onClick={onCreateInvoice}>
              {t("invoices.newInvoice")}
            </Button>
          )
          return renderCreate ? renderCreate(button) : button
        })()}
    </div>
  )
}
